import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Linkedin } from 'lucide-react';
import { TypingEffect } from '../components/TypingEffect';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HomePage() {
  const heroRef = useRef(null);
  const servicesRef = useRef(null);
  const verticalsRef = useRef(null);
  const workRef = useRef(null);
  const [latestInsights, setLatestInsights] = useState([]);

  useEffect(() => {
    fetch(`${API_URL}/api/insights`)
      .then(res => res.json())
      .then(data => setLatestInsights(data.slice(0, 3)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    const elements = [heroRef.current, servicesRef.current, verticalsRef.current, workRef.current];
    elements.forEach((el) => el && observer.observe(el));

    // Card slide-in observer
    const cardObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('slid-in');
          cardObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.8 });

    document.querySelectorAll('[data-slide]').forEach((el) => cardObserver.observe(el));

    return () => { observer.disconnect(); cardObserver.disconnect(); };
  }, []);

  const handleSmoothScroll = (targetId) => {
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    // Handle navigation clicks
    const handleNavClick = (e) => {
      const href = e.target.closest('a')?.href;
      if (href && href.includes('#')) {
        e.preventDefault();
        const targetId = href.split('#')[1];
        handleSmoothScroll(targetId);
      }
    };

    document.addEventListener('click', handleNavClick);
    return () => document.removeEventListener('click', handleNavClick);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="pt-28 pb-16 md:pt-56 md:pb-40 px-6 lg:px-8 opacity-0 translate-y-8 transition-all duration-1000 ease-out"
      >
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-3xl sm:text-5xl lg:text-7xl font-light tracking-tight text-black leading-[1.1] mb-4">
            we build <TypingEffect />
          </h1>
          <p className="text-xl sm:text-2xl lg:text-3xl font-light tracking-tight text-black/60 mb-6 md:mb-8">
            from zero to investor-ready.
          </p>
          <p className="text-base lg:text-xl font-normal text-gray-700 max-w-4xl mx-auto leading-relaxed">
            anyframe group is a fractional venture studio — we create the business model, 
            build the platform, and drive the revenue strategy for digital businesses.
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section 
        id="services"
        ref={servicesRef}
        className="py-16 md:py-24 px-4 md:px-6 lg:px-8 opacity-0 translate-y-8 transition-all duration-1000 ease-out"
        style={{ backgroundImage: `url('${process.env.REACT_APP_BACKGROUND_IMAGE_URL}')`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white mb-10 md:mb-16 text-center">
            what we do
          </h2>
          
          <div className="grid lg:grid-cols-2 gap-4 md:gap-8 mb-8 md:mb-16">
            {/* Service Card 1 */}
            <div data-slide="left" className="group bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg p-5 md:p-8">
              <h3 className="text-2xl font-light tracking-tight text-black mb-4">business design</h3>
              <p className="text-gray-700 leading-relaxed">
                vision, business model, and p&l from scratch
              </p>
            </div>

            {/* Service Card 2 */}
            <div data-slide="right" className="group bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg p-5 md:p-8">
              <h3 className="text-2xl font-light tracking-tight text-black mb-4">platform build</h3>
              <p className="text-gray-700 leading-relaxed">
                we design, orchestrate and ship digital platforms
              </p>
            </div>

            {/* Service Card 3 */}
            <div data-slide="left" className="group bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg p-5 md:p-8">
              <h3 className="text-2xl font-light tracking-tight text-black mb-4">investor decks</h3>
              <p className="text-gray-700 leading-relaxed">
                pitch decks and financial models that close rounds
              </p>
            </div>

            {/* Service Card 4 */}
            <div data-slide="right" className="group bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg p-5 md:p-8">
              <h3 className="text-2xl font-light tracking-tight text-black mb-4">revenue strategy</h3>
              <p className="text-gray-700 leading-relaxed">
                monetization architecture and go-to-market
              </p>
            </div>

            {/* Service Card 5 */}
            <div data-slide="left" className="group bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg p-5 md:p-8">
              <h3 className="text-2xl font-light tracking-tight text-black mb-4">project management</h3>
              <p className="text-gray-700 leading-relaxed">
                end-to-end coordination across teams and vendors
              </p>
            </div>

            {/* Service Card 6 */}
            <div data-slide="right" className="group bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg p-5 md:p-8">
              <h3 className="text-2xl font-light tracking-tight text-black mb-4">content & brand</h3>
              <p className="text-gray-700 leading-relaxed">
                content strategy, creation and editorial direction
              </p>
            </div>

            {/* Service Card 7 */}
            <div data-slide="left" className="group bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg p-5 md:p-8">
              <h3 className="text-2xl font-light tracking-tight text-black mb-4">referral & distribution systems</h3>
              <p className="text-gray-700 leading-relaxed">
                multi-tier referral and affiliate architectures for digital platforms
              </p>
            </div>

            {/* Service Card 8 */}
            <div data-slide="right" className="group bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg p-5 md:p-8">
              <h3 className="text-2xl font-light tracking-tight text-black mb-4">blockchain & token economy</h3>
              <p className="text-gray-700 leading-relaxed">
                wallet creation, token design, and web3 platform integration
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How We Work Section - Dark Background */}
      <section 
        id="howwework"
        ref={verticalsRef}
        className="py-10 md:py-14 px-4 md:px-6 lg:px-8 bg-white opacity-0 translate-y-8 transition-all duration-1000 ease-out"
      >
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-black mb-8 md:mb-16 text-center">
            how we work
          </h2>
          
          <div className="text-lg text-gray-700 leading-relaxed space-y-6">
            <p>
              we think like a consultancy, create like a studio, and build like a product company — all under one roof.
            </p>
            <p>
              anyframe group operates lean by design. We integrate AI into every stage of our workflow: from financial modeling and market research to platform prototyping and content production. Where others work in silos, we build in synthesis.
            </p>
            <p>
              We move fast without losing depth — from the earliest concept to a fully investor-ready venture.
            </p>
          </div>

          <p className="text-base md:text-lg text-gray-700 mt-6 md:mt-10 text-center tracking-wide">
            we move at 3x speed.
          </p>
        </div>
      </section>

      {/* Selected Work Section */}
      <section 
        id="work"
        ref={workRef}
        className="py-16 md:py-24 px-4 md:px-6 lg:px-8 opacity-0 translate-y-8 transition-all duration-1000 ease-out overflow-hidden relative"
      >
        <div className="absolute inset-0" style={{ backgroundImage: `url('${process.env.REACT_APP_BACKGROUND_IMAGE_URL}')`, backgroundSize: 'cover', backgroundPosition: 'center', transform: 'scale(-1, -1)' }} />
        <div className="max-w-6xl mx-auto relative">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white mb-10 md:mb-16 text-center">
            selected work
          </h2>
          
          <div className="grid lg:grid-cols-2 gap-4 md:gap-8">
            {/* Work Card 1 */}
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg p-5 md:p-8">
              <div className="inline-block bg-black text-white px-4 py-1.5 rounded-full text-sm font-bold mb-4">
                digital media
              </div>
              <h3 className="text-xl font-medium text-black mb-4">loyalty & rewards platform</h3>
              <p className="text-gray-700 leading-relaxed">
                cashback platform with 900+ brand partners. built from vision to launch: platform architecture, 
                membership model, and go-to-market strategy.
              </p>
            </div>

            {/* Work Card 2 */}
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg p-5 md:p-8">
              <div className="inline-block bg-black text-white px-4 py-1.5 rounded-full text-sm font-bold mb-4">
                igaming
              </div>
              <h3 className="text-xl font-medium text-black mb-4">igaming saas platform</h3>
              <p className="text-gray-700 leading-relaxed">
                b2b white-label sports betting network. business model, financial forecast, and platform 
                architecture for independent shop operators across multiple markets.
              </p>
            </div>

            {/* Work Card 3 */}
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg p-5 md:p-8">
              <div className="inline-block bg-black text-white px-4 py-1.5 rounded-full text-sm font-bold mb-4">
                tech / web3
              </div>
              <h3 className="text-xl font-medium text-black mb-4">sports investment platform</h3>
              <p className="text-gray-700 leading-relaxed">
                tokenized depositary receipt system for professional sports clubs with direct profit sharing, 
                two-tier referral distribution, and blockchain infrastructure.
              </p>
            </div>

            {/* Work Card 4 */}
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg p-5 md:p-8">
              <div className="inline-block bg-black text-white px-4 py-1.5 rounded-full text-sm font-bold mb-4">
                tech / digital media
              </div>
              <h3 className="text-xl font-medium text-black mb-4">web3 entertainment platform</h3>
              <p className="text-gray-700 leading-relaxed">
                membership-based event platform with multi-level access tiers, token economy, 
                and blockchain wallet integration.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Insights Section */}
      {latestInsights.length > 0 && (
        <section className="py-16 md:py-24 px-4 md:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-black mb-4 text-center">
              latest insights
            </h2>
            <div className="text-center mb-10 md:mb-16">
              <Link
                to="/insights"
                className="inline-block rounded-full px-6 py-2 bg-black text-white font-bold tracking-wide transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 hover:bg-black/90 text-sm"
              >
                view all
              </Link>
            </div>

            <div className="grid md:grid-cols-3 gap-4 md:gap-8">
              {latestInsights.map((article) => (
                <Link
                  key={article.slug}
                  to={`/insights/${article.slug}`}
                  className="group block bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg p-5 md:p-8 transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                >
                  <p className="text-sm text-gray-500 mb-3">{article.date}</p>
                  <h3 className="text-xl font-light tracking-tight text-black mb-3 group-hover:text-black/70 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-4 text-sm">{article.excerpt}</p>
                  <span className="inline-block bg-black text-white px-4 py-1.5 rounded-full text-sm font-bold">
                    read more
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact Section */}
      <section id="contact" className="py-16 md:py-24 px-4 md:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-black mb-4">
              let's build something.
            </h2>
            <p className="text-base md:text-lg text-gray-600 mb-8">
              tell us about your venture or challenge.
            </p>
            <a
              href="mailto:mail@anyframe.com?subject=Venture%20inquiry%20from%20anyframe.group"
              className="inline-block rounded-full px-8 py-2 bg-black text-white font-extrabold tracking-wide transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 hover:bg-black/90"
            >
              write us
            </a>
          </div>

          {/* Social Icons */}
          <div className="flex justify-center mt-12 pt-8 border-t border-gray-200">
            <a 
              href="https://www.linkedin.com/company/anyframe-group" 
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-black transition-colors duration-200"
              aria-label="LinkedIn"
            >
              <Linkedin className="h-5 w-5" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}